import React, { useState } from 'react';
import { adminAPI, exportsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Settings, MapPin, CreditCard, Mail, Phone, Clock, Globe, Send, Loader2, Star, Server, Download } from 'lucide-react';

export const SettingsTab = ({ sites, siteSettings, setSiteSettings }) => {
  const [savingSettings, setSavingSettings] = useState(false);
  const [testingEmail, setTestingEmail] = useState(null);
  const [exporting, setExporting] = useState(null);

  const getSettingsForSite = (siteId) => siteSettings.find(s => s.site_id === siteId) || {};
  const updateLocal = (siteId, field, value) => {
    setSiteSettings(prev => {
      const existing = prev.find(s => s.site_id === siteId);
      if (existing) return prev.map(s => s.site_id === siteId ? { ...s, [field]: value } : s);
      return [...prev, { site_id: siteId, [field]: value }];
    });
  };
  const save = async (siteId) => {
    setSavingSettings(true);
    try {
      await adminAPI.updateSettings(siteId, getSettingsForSite(siteId));
      toast.success('Sauvegardé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setSavingSettings(false);
  };
  const sendTest = async (siteId) => {
    setTestingEmail(siteId);
    try {
      const res = await adminAPI.testEmail(siteId);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setTestingEmail(null);
  };

  const handleExport = async (siteId, siteName) => {
    setExporting(siteId || 'all');
    try {
      const res = await exportsAPI.adminExportClients(siteId);
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      const scope = siteId ? siteId.replace('site-', '') : 'tous_sites';
      link.href = url;
      link.download = `riding_watt_export_${scope}_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Export ${siteName || 'global'} telecharge`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'export");
    }
    setExporting(null);
  };

  return (
    <div className="space-y-8" data-testid="admin-settings">
      {/* Global export - super admins only, franchise admins get per-site button */}
      <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Download className="w-5 h-5 text-[#00FF00]" />
            <div>
              <h3 className="font-heading text-lg font-bold text-white">Export donnees clients</h3>
              <p className="text-xs text-slate-400 mt-1">ZIP contenant : clients, reservations, paiements, participants, credits, cartes cadeaux (CSV UTF-8)</p>
            </div>
          </div>
          <Button
            onClick={() => handleExport(null, 'tous les sites')}
            disabled={exporting !== null}
            className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold"
            data-testid="export-all-clients-btn"
          >
            {exporting === 'all' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Exporter tous les sites
          </Button>
        </div>
      </div>

      {sites.map(site => {
        const s = getSettingsForSite(site.id);
        return (
          <div key={site.id} className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-6">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-[#00FF00]" />
                <h3 className="font-heading text-lg font-bold text-white">{site.name}</h3>
                <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-0 text-xs">{site.city}</Badge>
              </div>
              <Button
                onClick={() => handleExport(site.id, site.name)}
                disabled={exporting !== null}
                variant="outline"
                className="border-[#00FF00]/30 text-[#00FF00] hover:bg-[#00FF00]/10 text-xs"
                data-testid={`export-site-${site.id}`}
              >
                {exporting === site.id ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-2" />}
                Exporter ce site
              </Button>
            </div>

            {/* Paiement */}
            <div className="mb-6">
              <h4 className="text-sm font-bold text-white flex items-center gap-2 mb-4">
                <CreditCard className="w-4 h-4 text-[#00FF00]" /> Paiement
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-400">Fournisseur</Label>
                  <Select value={s.payment_provider || 'stripe'} onValueChange={v => updateLocal(site.id, 'payment_provider', v)}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid={`payment-provider-${site.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                      <SelectItem value="stripe">Stripe</SelectItem>
                      <SelectItem value="revolut">Revolut</SelectItem>
                      <SelectItem value="none">Aucun</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  {(!s.payment_provider || s.payment_provider === 'stripe') && (
                    <>
                      <Label className="text-xs text-slate-400">Clé API Stripe</Label>
                      <Input type="password" value={s.stripe_api_key || ''} onChange={e => updateLocal(site.id, 'stripe_api_key', e.target.value)} placeholder="sk_live_..." className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid={`stripe-key-${site.id}`} />
                    </>
                  )}
                  {s.payment_provider === 'revolut' && (
                    <>
                      <Label className="text-xs text-slate-400">Clé API Revolut</Label>
                      <Input type="password" value={s.revolut_api_key || ''} onChange={e => updateLocal(site.id, 'revolut_api_key', e.target.value)} placeholder="sk_..." className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid={`revolut-key-${site.id}`} />
                    </>
                  )}
                  {s.payment_provider === 'none' && (
                    <p className="text-xs text-slate-500 mt-6">Aucun fournisseur de paiement en ligne</p>
                  )}
                </div>
              </div>
            </div>

            {/* Email SMTP */}
            <div className="mb-6">
              <h4 className="text-sm font-bold text-white flex items-center gap-2 mb-4">
                <Mail className="w-4 h-4 text-[#00FF00]" /> Email (SMTP)
              </h4>
              <div className="flex items-center justify-between mb-4">
                <div><p className="text-sm text-white">Emails automatiques</p></div>
                <Switch checked={s.email_enabled || false} onCheckedChange={v => updateLocal(site.id, 'email_enabled', v)} />
              </div>
              {s.email_enabled && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-400 flex items-center gap-1"><Server className="w-3 h-3" /> Serveur SMTP</Label>
                      <Input value={s.smtp_host || ''} onChange={e => updateLocal(site.id, 'smtp_host', e.target.value)} placeholder="mail.riding-watt.com" className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid={`smtp-host-${site.id}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-400">Port SMTP</Label>
                      <Input type="number" value={s.smtp_port || 465} onChange={e => updateLocal(site.id, 'smtp_port', parseInt(e.target.value) || 465)} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid={`smtp-port-${site.id}`} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-400">Email SMTP (identifiant)</Label>
                      <Input value={s.smtp_email || ''} onChange={e => updateLocal(site.id, 'smtp_email', e.target.value)} placeholder="hyeres@riding-watt.com" className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid={`smtp-email-${site.id}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-400">Mot de passe SMTP</Label>
                      <Input type="password" value={s.smtp_password || ''} onChange={e => updateLocal(site.id, 'smtp_password', e.target.value)} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid={`smtp-password-${site.id}`} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-400">Expéditeur (affiché)</Label>
                      <Input value={s.sender_email || ''} onChange={e => updateLocal(site.id, 'sender_email', e.target.value)} placeholder="hyeres@riding-watt.com" className="bg-[#0A0A0A] border-[#2A2A2A] text-white" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-400">Email de notification (copie BCC)</Label>
                      <Input value={s.notification_email || ''} onChange={e => updateLocal(site.id, 'notification_email', e.target.value)} placeholder="votre-email@gmail.com" className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid={`notification-email-${site.id}`} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Objet confirmation</Label>
                    <Input value={s.confirm_email_subject || ''} onChange={e => updateLocal(site.id, 'confirm_email_subject', e.target.value)} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" />
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-300">Confirmation</p>
                    <Switch checked={s.confirm_email_enabled !== false} onCheckedChange={v => updateLocal(site.id, 'confirm_email_enabled', v)} />
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-300">Rappel 24h</p>
                    <Switch checked={s.reminder_email_enabled || false} onCheckedChange={v => updateLocal(site.id, 'reminder_email_enabled', v)} />
                  </div>
                  {s.smtp_host && s.smtp_email && (
                    <Button variant="outline" size="sm" onClick={() => sendTest(site.id)} disabled={testingEmail === site.id} className="text-[#00FF00] border-[#00FF00]/30" data-testid={`test-email-${site.id}`}>
                      {testingEmail === site.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Tester l'envoi
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Pied de page */}
            <div className="mb-6">
              <h4 className="text-sm font-bold text-white flex items-center gap-2 mb-4">
                <Globe className="w-4 h-4 text-[#00FF00]" /> Pied de page
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-400"><Phone className="w-3 h-3 inline mr-1" />Téléphone</Label>
                  <Input value={s.phone || ''} onChange={e => updateLocal(site.id, 'phone', e.target.value)} placeholder="+33..." className="bg-[#0A0A0A] border-[#2A2A2A] text-white" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400"><Mail className="w-3 h-3 inline mr-1" />Email contact</Label>
                  <Input value={s.footer_email || ''} onChange={e => updateLocal(site.id, 'footer_email', e.target.value)} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" />
                </div>
              </div>
              <div className="mt-4">
                <Label className="text-xs text-slate-400"><Clock className="w-3 h-3 inline mr-1" />Horaires</Label>
                <Input value={s.opening_hours || ''} onChange={e => updateLocal(site.id, 'opening_hours', e.target.value)} placeholder="Lun-Ven 9h-18h" className="bg-[#0A0A0A] border-[#2A2A2A] text-white" />
              </div>
              <div className="mt-4">
                <Label className="text-xs text-slate-400 flex items-center gap-1"><Star className="w-3 h-3 text-yellow-400" />Lien Google Reviews</Label>
                <Input value={s.google_review_url || ''} onChange={e => updateLocal(site.id, 'google_review_url', e.target.value)} placeholder="https://g.page/r/..." className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="google-review-url-input" />
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input type="checkbox" id={`review-email-${site.id}`} checked={s.review_email_enabled || false} onChange={e => updateLocal(site.id, 'review_email_enabled', e.target.checked)} className="accent-[#00FF00]" data-testid="review-email-toggle" />
                <Label htmlFor={`review-email-${site.id}`} className="text-xs text-slate-400 cursor-pointer">Envoyer email Google Reviews 24h après réservation</Label>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <Label className="text-xs text-slate-400">Instagram</Label>
                  <Input value={s.instagram_url || ''} onChange={e => updateLocal(site.id, 'instagram_url', e.target.value)} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Facebook</Label>
                  <Input value={s.facebook_url || ''} onChange={e => updateLocal(site.id, 'facebook_url', e.target.value)} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">TikTok</Label>
                  <Input value={s.tiktok_url || ''} onChange={e => updateLocal(site.id, 'tiktok_url', e.target.value)} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" />
                </div>
              </div>
            </div>

            <Button onClick={() => save(site.id)} disabled={savingSettings} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid={`save-settings-${site.id}`}>
              {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />} Sauvegarder
            </Button>
          </div>
        );
      })}
    </div>
  );
};
